import type { OrderScriptRow } from "@/features/marketplace/types/marketplace.types";

type NotifyLegsShape = {
  reviewOnly?: boolean;
  reviewOnlyReviewId?: string | null;
};

function parseReviewOnlyNotifyLegs(
  notifyLegs: OrderScriptRow["notifyLegs"],
): NotifyLegsShape | null {
  if (!notifyLegs || typeof notifyLegs !== "object") return null;
  const legs = notifyLegs as NotifyLegsShape;
  if (legs.reviewOnly !== true) return null;
  return legs;
}

function isReviewOnlyPendingNotify(
  script: Pick<
    OrderScriptRow,
    "notifyState" | "pendingNotifyStatus" | "publishAttemptId" | "notifyLegs"
  >,
): boolean {
  return (
    script.notifyState === "PENDING_NOTIFY" &&
    script.pendingNotifyStatus === "SUBMITTED_FOR_FEEDBACK" &&
    parseReviewOnlyNotifyLegs(script.notifyLegs) !== null &&
    Boolean(script.publishAttemptId?.endsWith(":review"))
  );
}

/** Pending review-only publish (add/replace slot) waiting for retry. */
export function isPartnerPreflightNotifyRetry(
  script: Pick<
    OrderScriptRow,
    | "notifyState"
    | "pendingNotifyStatus"
    | "preflightUploadId"
    | "publishAttemptId"
    | "notifyLegs"
    | "videos"
  >,
): boolean {
  if (script.notifyState !== "PENDING_NOTIFY") return false;
  if (script.pendingNotifyStatus !== "SUBMITTED_FOR_FEEDBACK") return false;

  if (isReviewOnlyPendingNotify(script)) {
    return true;
  }

  const videos = script.videos ?? [];
  const filledVideoCount = videos.filter((video) =>
    Boolean(video.preflightUploadId || video.scriptReviewId),
  ).length;
  const allocatedSlots = videos.length;

  if (allocatedSlots > 1 && filledVideoCount > 1) return false;
  if (allocatedSlots > 1 && filledVideoCount === 1) {
    return Boolean(script.preflightUploadId);
  }
  if (allocatedSlots > 1) return false;

  return Boolean(script.preflightUploadId);
}

/** Legacy line-level notify retry (not review-only per slot). */
export function isPartnerLegacyLineNotifyRetry(
  script: Pick<
    OrderScriptRow,
    | "notifyState"
    | "pendingNotifyStatus"
    | "publishAttemptId"
    | "scriptReviewId"
    | "fulfillmentStatus"
    | "notifyLegs"
  >,
): boolean {
  if (script.notifyState !== "PENDING_NOTIFY") return false;
  if (script.pendingNotifyStatus !== "SUBMITTED_FOR_FEEDBACK") return false;
  if (isReviewOnlyPendingNotify(script)) return false;
  return (
    Boolean(script.scriptReviewId?.trim()) &&
    script.fulfillmentStatus === "SUBMITTED_FOR_FEEDBACK"
  );
}

export function partnerVideoSlotPendingNotifyRetry(
  script: Pick<
    OrderScriptRow,
    | "notifyState"
    | "pendingNotifyStatus"
    | "publishAttemptId"
    | "notifyLegs"
  >,
  video: { scriptReviewId?: string | null },
): boolean {
  if (!isReviewOnlyPendingNotify(script)) return false;
  const reviewId = parseReviewOnlyNotifyLegs(script.notifyLegs)?.reviewOnlyReviewId;
  return Boolean(reviewId && video.scriptReviewId === reviewId);
}
